import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { DollarSign, Plus, Edit, CheckCircle } from 'lucide-react';

export default function PayrollManagement({ token }) {
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  
  const [createForm, setCreateForm] = useState({
    user_id: '',
    month: new Date().toISOString().slice(0, 7),
    bonus: '0',
    overtime_hours: '0',
    overtime_rate: '200',
    additional_allowances: '0',
    additional_deductions: '0',
    notes: ''
  });
  
  const [editForm, setEditForm] = useState({
    bonus: '0',
    overtime_hours: '0',
    overtime_rate: '200',
    additional_allowances: '0',
    additional_deductions: '0',
    status: 'pending',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [payrollRes, employeeRes] = await Promise.all([
        axios.get(`${API}/admin/payroll`, { headers }),
        axios.get(`${API}/admin/employees`, { headers })
      ]);
      setPayrolls(payrollRes.data);
      setEmployees(employeeRes.data);
    } catch (error) {
      console.error('Failed to fetch payroll data:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/payroll`, {
        ...createForm,
        bonus: parseFloat(createForm.bonus),
        overtime_hours: parseFloat(createForm.overtime_hours),
        overtime_rate: parseFloat(createForm.overtime_rate),
        additional_allowances: parseFloat(createForm.additional_allowances),
        additional_deductions: parseFloat(createForm.additional_deductions)
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Payroll entry created!');
      setCreateDialogOpen(false);
      setCreateForm({
        user_id: '',
        month: new Date().toISOString().slice(0, 7),
        bonus: '0',
        overtime_hours: '0',
        overtime_rate: '200',
        additional_allowances: '0',
        additional_deductions: '0',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create payroll entry');
    }
  };

  const openEditDialog = (payroll) => {
    setSelectedPayroll(payroll);
    setEditForm({
      bonus: payroll.bonus?.toString() || '0',
      overtime_hours: payroll.overtime_hours?.toString() || '0',
      overtime_rate: payroll.overtime_rate?.toString() || '200',
      additional_allowances: payroll.additional_allowances?.toString() || '0',
      additional_deductions: payroll.additional_deductions?.toString() || '0',
      status: payroll.status || 'pending',
      notes: payroll.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/payroll/${selectedPayroll.id}`, {
        bonus: parseFloat(editForm.bonus),
        overtime_hours: parseFloat(editForm.overtime_hours),
        overtime_rate: parseFloat(editForm.overtime_rate),
        additional_allowances: parseFloat(editForm.additional_allowances),
        additional_deductions: parseFloat(editForm.additional_deductions),
        status: editForm.status,
        notes: editForm.notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Payroll updated!');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update payroll');
    }
  };

  return (
    <Card className="p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
          <DollarSign className="h-5 w-5 mr-2 text-green-600" />
          Payroll Management
        </h3>
        
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700" data-testid="create-payroll-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Payroll Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Payroll Entry</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Employee</Label>
                <Select value={createForm.user_id} onValueChange={(value) => setCreateForm({...createForm, user_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Month</Label>
                <Input type="month" value={createForm.month} onChange={(e) => setCreateForm({...createForm, month: e.target.value})} required />
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Bonus (₹)</Label>
                  <Input type="number" value={createForm.bonus} onChange={(e) => setCreateForm({...createForm, bonus: e.target.value})} />
                </div>
                <div>
                  <Label>Extra Allowances (₹)</Label>
                  <Input type="number" value={createForm.additional_allowances} onChange={(e) => setCreateForm({...createForm, additional_allowances: e.target.value})} />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Overtime Hours</Label>
                  <Input type="number" value={createForm.overtime_hours} onChange={(e) => setCreateForm({...createForm, overtime_hours: e.target.value})} />
                </div>
                <div>
                  <Label>OT Rate (₹/hr)</Label>
                  <Input type="number" value={createForm.overtime_rate} onChange={(e) => setCreateForm({...createForm, overtime_rate: e.target.value})} />
                </div>
              </div>
              
              <div>
                <Label>Extra Deductions (₹)</Label>
                <Input type="number" value={createForm.additional_deductions} onChange={(e) => setCreateForm({...createForm, additional_deductions: e.target.value})} />
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea value={createForm.notes} onChange={(e) => setCreateForm({...createForm, notes: e.target.value})} placeholder="Additional notes..." />
              </div>
              
              <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                <Plus className="h-4 w-4 mr-2" />
                Create Payroll
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <ScrollArea className="h-96">
        <div className="space-y-3">
          {payrolls.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No payroll entries yet</p>
          ) : (
            payrolls.map((payroll) => (
              <div key={payroll.id} className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-800">{payroll.user_name}</p>
                    <p className="text-sm text-gray-600">{payroll.month}</p>
                    <Badge className={`mt-1 ${
                      payroll.status === 'paid' ? 'bg-green-500' :
                      payroll.status === 'approved' ? 'bg-blue-500' : 'bg-orange-500'
                    }`}>
                      {payroll.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEditDialog(payroll)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <Separator className="my-2" />
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Base Salary:</span>
                    <span className="font-medium">₹{payroll.base_salary?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bonus:</span>
                    <span className="font-medium text-green-600">+₹{payroll.bonus?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Days Present:</span>
                    <span className="font-medium">{payroll.days_present}/{payroll.days_worked}</span>
                  </div>
                  <Separator className="my-1" />
                  <div className="flex justify-between font-bold">
                    <span>Final Salary:</span>
                    <span className="text-green-600">₹{payroll.final_salary?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Payroll Entry</DialogTitle>
          </DialogHeader>
          {selectedPayroll && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm"><strong>{selectedPayroll.user_name}</strong></p>
                <p className="text-xs text-gray-600">{selectedPayroll.month}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Bonus (₹)</Label>
                  <Input type="number" value={editForm.bonus} onChange={(e) => setEditForm({...editForm, bonus: e.target.value})} />
                </div>
                <div>
                  <Label>Extra Allowances (₹)</Label>
                  <Input type="number" value={editForm.additional_allowances} onChange={(e) => setEditForm({...editForm, additional_allowances: e.target.value})} />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>OT Hours</Label>
                  <Input type="number" value={editForm.overtime_hours} onChange={(e) => setEditForm({...editForm, overtime_hours: e.target.value})} />
                </div>
                <div>
                  <Label>OT Rate (₹/hr)</Label>
                  <Input type="number" value={editForm.overtime_rate} onChange={(e) => setEditForm({...editForm, overtime_rate: e.target.value})} />
                </div>
              </div>
              
              <div>
                <Label>Extra Deductions (₹)</Label>
                <Input type="number" value={editForm.additional_deductions} onChange={(e) => setEditForm({...editForm, additional_deductions: e.target.value})} />
              </div>
              
              <div>
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(value) => setEditForm({...editForm, status: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} />
              </div>
              
              <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-2" />
                Update Payroll
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}